<template>
  <div
    class="article-view"
    :class="{
      minimized: minimizeable && minimized,
      'full-view': !minimizeable,
      'is-embed-card': model.is_url,
      'is-faded': isFaded,
    }"
  >
    <div class="columns is-mobile">
      <div class="column">
        <p v-if="model.author && (model.category != null || canManage)">
          <a :href="model.author.url">{{ model.author.short_name }}</a>
        </p>
        <p v-if="model.published_at">{{ publishedAtLabel }}</p>
      </div>
      <div class="column is-half quick-actions">
        <div
          class="quick-info"
          v-if="model.send_email && model.email_recipients_count > 0"
          :title="'Отправлено ' + model.email_recipients_count + ' уведомлений'"
        >
          <i class="mdi mdi-email"></i>
          <span>{{ model.email_recipients_count }}</span>
        </div>
        <a v-if="canManage" class="quick-action" :href="editUrl">
          <i class="mdi mdi-feather" title="Редактировать"></i>
        </a>
      </div>
    </div>

    <div class="tags" v-if="canManage || tags.length > 0">
      <template v-if="canManage">
        <div class="tag is-info" v-if="model.published_at == null">
          Не опубликовано
        </div>
        <div class="tag is-success" v-else>Опубликовано</div>
      </template>
      <a
        class="tag"
        :class="{
          'is-primary': isActiveTag(tag),
        }"
        v-for="tag in tags"
        :key="tag.hash"
        @click="(e) => onTagClick(e, tag)"
      >
        {{ tag.name }}
      </a>
    </div>

    <h5
      v-if="!model.is_url"
      @click="onOpen"
      class="article-title subtitle is-size-5"
    >
      {{ postTitleText }}
    </h5>

    <embed-card v-if="model.is_url" :og-tags="model.og_tags" />

    <div v-else @click="onOpen" class="article-content" v-html="content"></div>

    <attachments
      v-if="model.has_attachments"
      upload-position="is-top"
      :storage-indicator="false"
      :show-author="false"
      :show-header="false"
      :model="{
        id: model.id,
        type: 'article',
      }"
    ></attachments>

    <div class="footer">
      <div class="read-more">
        <a
          target="_blank"
          v-if="model.is_url"
          :href="$_.get(model, 'og_tags.url')"
          class="button is-primary is-outlined"
        >
          <span>Читать источник</span>
          <span class="icon is-small">
            <i class="mdi mdi-open-in-new"></i>
          </span>
        </a>
        <button v-else @click="onOpen" class="button is-primary is-outlined">
          <span>Читать <span class="is-hidden-mobile">далее</span></span>
          <span class="icon is-small">
            <i class="mdi mdi-chevron-double-right"></i>
          </span>
        </button>
      </div>
      <div v-if="externalSource" class="external-source">
        <a target="_blank" :href="externalSource.url" class="button">
          <span class="icon is-small">
            <img
              v-if="externalSource.icon"
              :src="externalSource.icon"
              :alt="externalSource.name"
            />
            <i v-else class="mdi mdi-open-in-new"></i>
          </span>
          <span class="name">{{ externalSource.name }}</span>
        </a>
      </div>
      <div class="gutter"></div>
      <div
        v-if="model.published_at && currentSection != 'media'"
        @click="onParticipate"
        class="attendance has-text-grey-light"
        :class="{ attended }"
      >
        <b-tooltip
          type="is-light"
          :label="attended ? 'Мое событие' : 'Участие в событии'"
        >
          <b-icon v-if="attended" icon="account-multiple-check"></b-icon>
          <b-icon v-else icon="account-multiple-plus"></b-icon>
        </b-tooltip>
      </div>
      <div
        v-if="model.published_at"
        @click="onLike"
        class="likes has-text-grey-light"
        :class="{ liked: isLiked }"
      >
        <b-tooltip
          type="is-light"
          :label="isLiked ? 'В избранном' : 'Добавить в избранное'"
        >
          <b-icon v-if="isLiked" icon="heart"></b-icon>
          <b-icon v-else icon="heart-outline"></b-icon>
        </b-tooltip>
        <span class="text">{{ model.likes }}</span>
      </div>
      <div v-if="model.published_at" class="views has-text-grey-light">
        <b-icon icon="eye-outline"></b-icon>
        <span class="text">{{ model.views }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import messages from "../../../mixins/messages";
import toasts from "../../../mixins/toasts";

import EmbedCard from "./EmbedCard.vue";

import { ResizeObserver } from "resize-observer";
import trimHtml from "trim-html";

function convertRemToPixels(rem) {
  return rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
}

const htmlTrimCharacterCount = 250;

const externalSourceHostnameAliases = {
  "fmp.msu.ru": "ФМП МГУ",
};

export default Vue.extend({
  mixins: [messages, toasts],
  props: {
    value: {
      type: Object,
      required: true,
    },
    canMinimize: {
      type: Boolean,
      default: true,
    },
    activeTags: {
      type: Array,
      default: () => [],
    },
    canClickTags: {
      type: Boolean,
      default: true,
    },
    currentSection: String,
  },
  components: {
    EmbedCard,
  },
  data() {
    return {
      endpoint: "/api/news",
      minimized: false,
      model: this.value,
      isLiked: this.value.is_liked,
      attended: this.value.attended,
      isFaded: false,
    };
  },
  mounted() {
    this.minimized =
      this.canMinimize && (this.trimmedContent.more || this.model.is_url);
  },
  computed: {
    tags() {
      return _.get(this.model, "tags", []);
    },
    editUrl() {
      return `/news/${this.model.id}/edit`;
    },
    canManage() {
      return (
        this.$can("manage articles") ||
        this.$user.alias == _.get(this.model.author, "alias")
      );
    },
    publishedAtLabel() {
      return this.$moment(this.model.published_at).format("DD MMMM[,] HH:mm");
    },
    postTitleText() {
      const ogTitle = _.get(this.model.og_tags, "title", "");

      if (this.model.is_url && ogTitle.length > 0) {
        return ogTitle;
      } else {
        return this.model.title;
      }
    },
    minimizeable() {
      return this.canMinimize; // && !this.model.is_url;
    },
    trimmedContent() {
      return trimHtml(this.model.content, { limit: htmlTrimCharacterCount });
    },
    content() {
      return this.minimized ? this.trimmedContent.html : this.model.content;
    },
    externalSource() {
      let originalUrl = _.get(this.model.external_source, "url");
      if (this.model.external_source) {
        try {
          let icon = _.get(this.model.external_source, "icon");
          let url = new URL(_.get(this.model.external_source, "url"));

          const alias =
            externalSourceHostnameAliases[url.hostname] || url.hostname;
          let name = _.get(this.model.external_source, "name", alias);

          return { url, name, icon };
        } catch (e) {
          console.warn(`Unable to get host for url '${originalUrl}'`);
        }
      }

      return null;
    },
  },
  methods: {
    isActiveTag(tag) {
      return this.activeTags.includes(tag.hash);
    },
    onTagClick(e, tag) {
      if (this.canClickTags) {
        this.$emit("tag-click", tag, e.ctrlKey);
      }
    },
    onOpen(e) {
      if (this.minimized) {
        this.$emit("open", this.model);
      }
    },
    async onLike() {
      this.isLiked = !this.isLiked;
      this.model.likes += this.isLiked ? 1 : -1;

      await axios({
        method: this.isLiked ? "post" : "delete",
        headers: { Authorization: `Bearer ${this.$jwt}` },
        url: `//${process.env.MIX_LYNX_API_HOST}/news/${this.model.id}/likes`,
      });
    },
    async onParticipate() {
      this.attended = !this.attended;

      if (this.attended == false && this.currentSection == "my-events") {
        this.isFaded = true;
      } else {
        this.isFaded = false;
      }

      await axios({
        method: this.attended ? "post" : "delete",
        headers: { Authorization: `Bearer ${this.$jwt}` },
        url: `//${process.env.MIX_LYNX_API_HOST}/news/${this.model.id}/attendance`,
      });
    },
    async onView() {
      await axios({
        method: "post",
        headers: { Authorization: `Bearer ${this.$jwt}` },
        url: `//${process.env.MIX_LYNX_API_HOST}/news/${this.model.id}/views`,
      });
    },
    publish() {
      let loading = this.$loading.open();

      axios
        .put(`${this.endpoint}/${this.model.id}/publish`)
        .then(({ data }) => {
          this.model = data;
          this.toast("success", "Новость была успешно опубликована");
        })
        .catch((error) => {
          this.toast("error", "@unexpected", error);
        })
        .finally(() => {
          loading.close();
        });
    },
  },
});
</script>

<style scoped lang="sass">
@import "../../../../../sass/app"

.article-view
    position: relative
    &.is-faded
        .article-content,
        .article-title,
        .read-more,
        .external-source
            opacity: .5
    &:not(.full-view)
        overflow: hidden
        margin-bottom: 1.5rem
        padding-bottom: 1rem
    &.full-view
        .external-source
            margin-left: 0
    &.minimized
        .article-content,
        .article-title
            cursor: pointer
        .read-more
            display: block !important
        &::after
            content: ''
        .article-content
            margin-bottom: 1rem
    &:not(:last-child)
        border-bottom: 1px solid $grey-lighter

    .header
        .title
            font-weight: initial

    .read-more
        display: none
        margin-right: 0.5rem

    .read-more,
    .external-source
        .button
            +tablet
                & > span
                    height: 1.625rem
            +mobile
                border-radius: 2px
                font-size: 0.75rem

    &:not(.full-view)
        .external-source
            max-width: 40%
            .button
                width: 100%
            +mobile
                @media(max-width: 400px)
                    width: 25%

    .external-source
        .button
            padding-left: 0.25rem
            .name
                overflow: hidden
                text-overflow: ellipsis
            .icon
                margin-left: 0 !important
                img
                    max-width: fit-content

    .quick-actions
        display: flex
        justify-content: flex-end
        > *
            &:not(:last-child)
                margin-right: 1rem
        .quick-action
            opacity: .75
            color: $primary
            font-size: 2rem // Twice as big
            &:hover
                opacity: 1
                cursor: pointer
        .quick-info
            cursor: help
            color: $grey-light
            align-self: flex-end
    .status-icon
        color: $grey
        opacity: .75
        cursor: help
        &.status-published
            color: $success

    .footer
        background: initial
        padding: 0
        margin-top: 1rem
        display: flex
        align-items: center
        justify-content: center
        & > *
            &.gutter
                flex-grow: 1
            &.attendance,
            &.views,
            &.likes
                flex-grow: 0
                +tablet
                    margin-right: .5rem
                    &:not(:last-child)
                        margin-right: 1.5rem
                +mobile
                    &:not(:last-child)
                        margin-right: .5rem
            &.attendance,
            &.likes
                cursor: pointer
                // &.liked
                //     .icon
                //         color: $danger !important
                //         font-size: 20px !important
                //         .mdi
                //             font-size: 20px !important
                //             // animation-duration: 3s
                //             // animation-name: slidein
                //     .text
                //         color: $text !important
            &.likes.liked .icon
                color: $danger !important
            &.attendance.attended .icon
                color: $info !important
            .text
                vertical-align: top
            +mobile
                .text
                    font-size: 0.95rem
                .icon
                    height: 1rem
                    width: 1rem
                    ::before
                        font-size: 18px !important

@keyframes likes-pop
    0%
        font-size: .5rem
    75%
        font-size: 1.5rem
    100%
        font-size: 1rem
</style>

<style lang="sass">
.article-view
    .article-content p
        margin-top: 1em !important
    .attachments .table
        margin-bottom: 0
</style>




// WEBPACK FOOTER //
// resources/assets/js/components/views/news/app/Post.vue